// ═══════════════════════════════════════════════════════════
// usePresence — хук для відстеження хто редагує документ
// Записує presence при відкритті модалки, видаляє при закритті
// Повертає { editingUser, isLocked } — хто зараз редагує (крім мене)
// TTL: ігноруємо записи старші 5 хвилин (zombie cleanup)
// ═══════════════════════════════════════════════════════════
import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const PRESENCE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * @param {string} docType  - тип документа: 'appointment' | 'customer'
 * @param {string|number|null} docId - id документа (null = новий, presence не реєструємо)
 */
export function usePresence(docType, docId) {
  const { currentUser, userProfile } = useAuth();
  const [editingUser, setEditingUser] = useState(null);
  const cleanupDone = useRef(false);

  const presenceId = docId ? `${docType}_${docId}` : null;
  const myUid = currentUser?.uid;
  const myName = userProfile?.displayName || currentUser?.email || 'Користувач';

  useEffect(() => {
    if (!presenceId || !myUid) return;
    cleanupDone.current = false;

    async function checkAndRegister() {
      try {
        // Перевіряємо чи хтось вже редагує
        const res = await apiFetch('/api/collection?name=_presence');
        const allPresence = res.data || [];
        const now = Date.now();
        const other = allPresence.find(p =>
          p.id === presenceId &&
          p.uid !== myUid &&
          p.openedAt > now - PRESENCE_TTL
        );
        if (!cleanupDone.current) {
          setEditingUser(other || null);
          if (other) {
            const remaining = PRESENCE_TTL - (now - other.openedAt);
            setTimeout(() => {
              if (!cleanupDone.current) setEditingUser(null);
            }, remaining);
          }
        }

        // Реєструємо власну presence
        await apiFetch('/api/collection?name=_presence', {
          method: 'POST',
          body: { id: presenceId, uid: myUid, displayName: myName, openedAt: now },
        });
      } catch (err) {
        console.warn('[usePresence] error:', err);
      }
    }

    checkAndRegister();

    return () => {
      cleanupDone.current = true;
      apiFetch(`/api/collection?name=_presence&id=${encodeURIComponent(presenceId)}`, {
        method: 'DELETE',
      }).catch(() => {});
    };
  }, [presenceId, myUid, myName]);

  return { editingUser, isLocked: !!editingUser };
}
